/**
 * AppHeader — shared top bar for authenticated pages.
 *
 * Replaces the per-page inline headers that used to duplicate user name,
 * sync status, sign-out button, etc. across each page. Bell icon, the
 * universal patient/page search, and the profile avatar menu all mount
 * here so they're identical and present on every screen (Settings keeps
 * its own bespoke header/search, since that search is settings-card-scoped
 * rather than the universal one).
 *
 * Pages render it like:
 *
 *     <AppHeader title="Patients" />
 *     <AppHeader title="Dashboard">
 *       <DownloadReportButton userRole={role} />
 *     </AppHeader>
 *
 * `children` is a slot for per-page right-side actions (e.g. "Download
 * Report" on Dashboard, "Export CSV" on a report page, etc.). Rendered to
 * the left of the bell so the bell/avatar stay in a predictable spot.
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "aws-amplify/auth";

import { SettingsAPI } from "../../services/settings.service";
import { BellIcon } from "../notifications/BellIcon";
import { GlobalSearchBar, SearchMode } from "./GlobalSearchBar";
import swasthiqSymbol from "../../assets/kaagazy-symbol.png";
import { WalletBalanceWidget } from "../credits/WalletBalanceWidget";

interface AppHeaderProps {
  /** Page title shown on the left side. */
  title: string;
  /** Optional per-page right-side actions (e.g. download / export buttons). */
  children?: React.ReactNode;
  /** What the search bar searches — defaults to patients-only. Doctors
   * page searches doctors only; Appointments-related pages search both. */
  searchMode?: SearchMode;
}

export const AppHeader: React.FC<AppHeaderProps> = ({ title, children, searchMode = "patients" }) => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState<string | undefined>();
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    SettingsAPI.getProfile()
      .then(p => {
        const full = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim();
        if (full) setUserName(full);
        setAvatarUrl(p.photo_url ?? undefined);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const handleProfile = () => {
    setMenuOpen(false);
    navigate("/settings", { state: { section: "profile" } });
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await signOut();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  return (
    <header
      className="min-h-14 border-b border-slate-200 bg-white flex flex-nowrap items-center justify-between gap-x-3 px-4 sm:px-6 py-2 sm:py-0 sticky z-20"
      style={{ top: "var(--offline-banner-h, 0px)" }}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1 ml-12 lg:ml-0">
        <span className="text-sm font-semibold text-slate-800 truncate shrink-0">{title}</span>
        <GlobalSearchBar mode={searchMode} />
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <WalletBalanceWidget />
        <BellIcon />
        {children}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={userName ?? "Account"}
            className="block rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <img
              src={avatarUrl || swasthiqSymbol}
              alt={userName ?? "avatar"}
              className="w-8 h-8 rounded-full object-cover bg-white ring-2 ring-slate-200"
            />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg ring-1 ring-black/5 py-1 z-30 transform-gpu"
            >
              {userName && (
                <div className="px-3 py-2 border-b border-gray-50">
                  <p className="text-xs font-medium text-gray-900 truncate">{userName}</p>
                </div>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={handleProfile}
                className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className="text-gray-400">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                Profile
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
